import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import SectionCard from './SectionCard';
import SsExternalProgressPanel from './SsExternalProgressPanel';

const FLOW_LABEL = {
  no_job: 'No visit',
  scheduling: 'Scheduling',
  scheduled: 'Scheduled',
  progress: 'In progress',
  completed: 'Done',
};

/**
 * Building overview: external partners + SS cards. Click opens progress panel.
 */
export default function BuildingExternalPartnersSection({ buildingId }) {
  const [open, setOpen] = useState(null); // { partnerId, ssCode }

  const { data, isLoading } = useQuery({
    queryKey: ['building-external-board', buildingId],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/external-board`)).data?.data,
    enabled: !!buildingId,
  });

  const partners = data?.partners || [];
  if (isLoading || partners.length === 0) return null;

  if (open?.partnerId && open?.ssCode) {
    return (
      <div className="mb-6">
        <SsExternalProgressPanel
          partnerId={open.partnerId}
          buildingId={buildingId}
          ssCode={open.ssCode}
          onClose={() => setOpen(null)}
        />
      </div>
    );
  }

  return (
    <SectionCard title="External partners" className="mb-6">
      <p className="text-xs text-gray-600 -mt-1 mb-2">
        Linked by Service mandates or SS Default IQP. Open an SS for mail and schedule progress.
      </p>
      <div className="space-y-4">
        {partners.map((p) => (
          <div key={p.partner_id} className="rounded-lg border border-green-200 bg-white/70 p-3">
            <div className="flex items-start gap-2 mb-2">
              <Building2 className="w-4 h-4 text-navy-800 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900">{p.partner_name}</p>
                <p className="text-xs text-gray-500">
                  {(p.link_sources || [])
                    .map((s) => (s === 'mandate' ? 'Service mandate' : 'Default IQP'))
                    .join(' · ')}
                  {p.summary?.overdue
                    ? ` · ${p.summary.overdue} overdue`
                    : p.summary?.waiting
                      ? ` · ${p.summary.waiting} waiting`
                      : ''}
                </p>
              </div>
            </div>
            <ul className="grid gap-2 sm:grid-cols-2">
              {(p.ss_items || []).map((ss) => (
                <li key={ss.ss_code}>
                  <button
                    type="button"
                    onClick={() => setOpen({ partnerId: p.partner_id, ssCode: ss.ss_code })}
                    className="w-full text-left rounded-lg border border-gray-200 bg-green-50/80 hover:bg-green-100 px-3 py-2 flex items-center gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm">{ss.ss_code}</p>
                      <p className="text-xs text-gray-600 truncate">
                        {FLOW_LABEL[ss.flow] || ss.flow}
                        {ss.current_label ? ` · ${ss.current_label}` : ''}
                        {ss.scheduled_date ? ` · ${ss.scheduled_date}` : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
